/** Community - member posts, replies, likes. List + detail in one page. */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Heart, Plus, ArrowLeft, Trash2, Users, Loader2 } from "lucide-react";

const CATEGORY_LABELS = {
  general: "General",
  study_tips: "Study tips",
  exam_experience: "Exam experience",
  wins: "Wins 🎉",
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const NewPostForm = ({ onCreated, onCancel }) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("general");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/community/posts", { title, body, category });
      toast.success("Posted to the community");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="community-new-post-form">
      <div className="flex flex-wrap gap-2">
        {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setCategory(k)}
            className={`text-xs px-3 py-1.5 rounded-full border ${category === k ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground"}`}>
            {label}
          </button>
        ))}
      </div>
      <Input data-testid="community-post-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title - e.g. How I fixed my pharmacology accuracy" required minLength={4} maxLength={160} />
      <Textarea data-testid="community-post-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Share your tip, question or experience…" required minLength={10} maxLength={5000} rows={4} />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
        <Button type="submit" size="sm" disabled={saving} data-testid="community-post-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post"}
        </Button>
      </div>
    </form>
  );
};

const PostDetail = ({ postId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get(`/community/posts/${postId}`).then(setData).catch(() => {
      toast.error("Post not found");
      navigate("/community");
    });
  }, [postId, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-16 text-center text-muted-foreground">Loading…</div>;
  const { post, replies, can_moderate } = data;

  const sendReply = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/community/posts/${postId}/replies`, { body: reply });
      setReply("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reply");
    } finally {
      setSending(false);
    }
  };

  const like = async () => {
    try { await api.post(`/community/posts/${postId}/like`); load(); } catch {}
  };

  const delPost = async () => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/community/posts/${postId}`); toast.success("Deleted"); navigate("/community"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const delReply = async (rid) => {
    if (!window.confirm("Delete this reply?")) return;
    try { await api.delete(`/community/replies/${rid}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="community-post-detail">
      <button onClick={() => navigate("/community")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Back to community
      </button>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <span className="text-xs px-2.5 py-1 rounded-full bg-secondary text-muted-foreground">{CATEGORY_LABELS[post.category] || post.category}</span>
            <h1 className="text-xl font-bold mt-3">{post.title}</h1>
            <p className="text-xs text-muted-foreground mt-1">{post.author_name} · {timeAgo(post.created_at)}</p>
          </div>
          {(post.is_mine || can_moderate) && (
            <button onClick={delPost} data-testid="community-delete-post-btn" className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="text-sm mt-4 whitespace-pre-wrap">{post.body}</p>
        <button onClick={like} data-testid="community-like-btn"
          className={`mt-4 inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border ${post.liked_by_me ? "border-destructive/40 bg-destructive/10 text-destructive" : "border-border text-muted-foreground hover:text-foreground"}`}>
          <Heart className={`w-4 h-4 ${post.liked_by_me ? "fill-current" : ""}`} /> {post.like_count}
        </button>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold">{replies.length} repl{replies.length === 1 ? "y" : "ies"}</h3>
        {replies.map((r) => (
          <div key={r.id} className="rounded-xl border border-border bg-card p-4 flex items-start justify-between gap-3" data-testid="community-reply">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">{r.author_name} · {timeAgo(r.created_at)}</p>
              <p className="text-sm mt-1 whitespace-pre-wrap">{r.body}</p>
            </div>
            {(r.is_mine || can_moderate) && (
              <button onClick={() => delReply(r.id)} className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive flex-shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
        <form onSubmit={sendReply} className="flex gap-2" data-testid="community-reply-form">
          <Input data-testid="community-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" required minLength={2} maxLength={3000} />
          <Button type="submit" size="sm" disabled={sending || reply.length < 2} data-testid="community-reply-submit" className="px-5">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reply"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default function CommunityPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [posts, setPosts] = useState(null);
  const [category, setCategory] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(() => {
    const qs = category ? `?category=${category}` : "";
    api.get(`/community/posts${qs}`).then((d) => setPosts(d.posts)).catch(() => toast.error("Could not load community"));
  }, [category]);

  useEffect(() => { if (!postId) load(); }, [postId, load]);

  if (postId) return <PostDetail postId={postId} />;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="community-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold inline-flex items-center gap-2">
            <Users className="w-7 h-7 text-primary" /> Community
          </h1>
          <p className="text-muted-foreground mt-1">Tips, questions and wins from fellow ADC candidates.</p>
        </div>
        <Button size="sm" className="gap-2" onClick={() => setShowNew(!showNew)} data-testid="community-new-post-btn">
          <Plus className="w-4 h-4" /> New post
        </Button>
      </div>

      {showNew && <NewPostForm onCreated={() => { setShowNew(false); load(); }} onCancel={() => setShowNew(false)} />}

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setCategory(null)} className={`text-xs px-3 py-1.5 rounded-full border ${!category ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground"}`}>All</button>
        {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
          <button key={k} onClick={() => setCategory(k)} className={`text-xs px-3 py-1.5 rounded-full border ${category === k ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {!posts ? (
        <div className="py-16 text-center text-muted-foreground">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="community-empty">
          <p className="font-medium text-foreground">Be the first to post</p>
          <p className="text-sm mt-1">Share a study tip, ask a question, or celebrate a win.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <button key={p.id} onClick={() => navigate(`/community/${p.id}`)}
              data-testid={`community-post-${p.id}`}
              className="w-full text-left rounded-2xl border border-border bg-card p-5 hover:border-primary/40 transition-colors">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="px-2 py-0.5 rounded-full bg-secondary">{CATEGORY_LABELS[p.category] || p.category}</span>
                <span>{p.author_name}</span>·<span>{timeAgo(p.created_at)}</span>
              </div>
              <h3 className="font-semibold mt-2">{p.title}</h3>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{p.body}</p>
              <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {p.reply_count}</span>
                <span className={`inline-flex items-center gap-1 ${p.liked_by_me ? "text-destructive" : ""}`}><Heart className={`w-3.5 h-3.5 ${p.liked_by_me ? "fill-current" : ""}`} /> {p.like_count}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
